import { useState, useEffect } from "react";
import { useCoreStateContext } from 'spark-core-dx/contexts';
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { useGenericManager } from 'spark-core-dx/hooks';

export const useDashboard = (activeTabId) => {
    const [dashboardList, setDashboardList] = useState([]);
    const itemServiceFactory = new ItemServiceFactory();
    const dashboardMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Dashboard);
    const dashboardDatasetMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DashboardDataset);
    const visualTypeMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.VisualType);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const coreStateContext = useCoreStateContext();


     const getDashboardVisualOptionsList = async (ids, draft) => {
           const result = await dashboardMgr.RunOperation({ operationName: "LoadVisualOptions", queryParams: {ids,draft}});
           const key = "VisualOptionShells" + (draft ? "_Draft" : "");
           if(result.Success){
                coreStateContext.replace(result.Items,key);
            }
            else{
                coreStateContext.replace([],key);
            }
            
            return result;
     };




    const getDashboardList = async () => {
        const result = await dashboardMgr.Query({model:"x => x.IsActive == true && x.IsDeleted == false"});
        if(result.Success){
            setDashboardList(result?.Items ?? []);
        }
        else{
            setDashboardList([]);    
        }
        return result;
    };

    const loadVisualTypesList = async () => {
        var result = await visualTypeMgr.Query({model:"x => x.IsActive == true && x.IsDeleted == false"});
        if(result.Success){
            coreStateContext.set(result.Items,"VisualTypes");
        }
        else{
            coreStateContext.set([],"VisualTypes");
        }

        return result;
    };

    const loadDatasetList = async () => {
        var result = await dashboardDatasetMgr.Query({model:"x => x.IsActive == true && x.IsDeleted == false"});
        if(result.Success){
            coreStateContext.set(result.Items,"DashboardDatasets");
        }
        else{
            coreStateContext.set([],"DashboardDatasets");
        }

        return result;
    };

    const updateDashboard = async (dashboard) => {
        return await dashboardMgr.saveItem(dashboard);
    };
    
    const updateVisualPosition = async (visualsPositions) => {
        return await dashboardMgr._save(visualsPositions,"VisualPosition");
    };
    // Initialization
    useEffect(() => {
       const initializeDashboards = async () => {
            setIsLoading(true);
            try {
                    const [p1, p2, p3] = await Promise.allSettled([
                        loadDatasetList(),
                        loadVisualTypesList(),
                        getDashboardList(),
                    ]);

                    if (p3.status === "fulfilled" && p3.value?.Items && p3.value?.Items?.length > 0) {
                        const [vo1,vo2] = await Promise.allSettled([
                            getDashboardVisualOptionsList(p3.value?.Items.map(x=>x._id) ?? [], false),
                            getDashboardVisualOptionsList(p3.value?.Items.map(x=>x._id) ?? [], true )
                        ]);

                        if ((p1.status === "rejected" && p1.value?.Success) || 
                            (p2.status === "rejected" && p2.value?.Success) || 
                            (p3.status === "rejected" && p3.value?.Success) ||
                            (vo1.status == "rejected" && vo1.value?.Success) || 
                            (vo2.status == "rejected" && vo2.value?.Success)
                            ) {
                            setHasError(true);
                        }
                    }
                    else{ setHasError(true); }
            } finally {
                setIsLoading(false);
            }
        };

        initializeDashboards();
    }, []);

    const activeDashboard = dashboardList?.find((x) => x?._id === activeTabId) ?? null;

    const saveAndSetDashboard = (dashboardPatch, visualOnly) => {
        return new Promise((resolve, reject) => {
            // preserve _id and merge patches (fields now PascalCase)
            if(visualOnly){
                const updatedDashboardVisuals = {Visuals:dashboardPatch.DraftDetails.Visuals.map((x)=>{return {
                    VisualId: x.VisualId,
                    PositionX : x.PositionX,
                    PositionY : x.PositionY,
                    Width : x.Width,
                    Height : x.Height,
                    IsLocked : x.IsLocked,
                    ZIndex : x.ZIndex
                } }),UniqueId: activeDashboard?._id, _id: activeDashboard?._id };
            updateVisualPosition(updatedDashboardVisuals)
                .then((response) => {       
                    if (response?.Success) {
                        const dbDashboard = response.Items.first();
                        setDashboardList((prev) => prev.map((d) => (d._id === dbDashboard._id ? dbDashboard : d)));
                        resolve(200);
                    } else {
                        console.error("Failed to save dashboard.");
                        reject(500);
                    }
                })
                .catch(reject);
            }
            else{
            const updatedDashboard = { ...activeDashboard, ...dashboardPatch,UniqueId: activeDashboard?._id, _id: activeDashboard?._id };
            updateDashboard(updatedDashboard)
                .then((response) => {       
                    if (response?.Success) {
                        const dbDashboard = response.Items.first();
                        setDashboardList((prev) => prev.map((d) => (d._id === dbDashboard._id ? dbDashboard : d)));
                        resolve(200);
                    } else {
                        console.error("Failed to save dashboard.");
                        reject(500);
                    }
                })
                .catch(reject);
            }
        });
    };

    const addNewDashToFullList = (dashboard) => {
        setDashboardList((prev) => [...prev, dashboard]);
    };

    const removeActiveDashFromList = (dashboardId) => {
        setDashboardList((prev) => prev.filter((d) => d._id !== dashboardId));
    };

    const clearAllVisuals = () => {
        saveAndSetDashboard({
            DraftDetails: { ...activeDashboard?.DraftDetails, Visuals: [] },
        });
    };

    const revertChanges = () => {
        saveAndSetDashboard({
            DraftDetails: { ...activeDashboard?.PublishedDetails },
        });
    };

    const handleDashNameChange = (name) => {
        saveAndSetDashboard({
            DraftDetails: { ...activeDashboard?.DraftDetails, Name: name },
        });
    };

    const handleDashDescriptionChange = (desc) => {
        saveAndSetDashboard({
            DraftDetails: {
                ...activeDashboard?.DraftDetails,
                Description: desc,
            },
        });
    };

    const handleDashSmallChange = async (icon,columnCount) => {
        return await saveAndSetDashboard({
            DraftDetails: { ...activeDashboard?.DraftDetails, Icon: icon, ColumnCount: columnCount },
        });
    };

    const publishDashboard = async (publicState) => {

        var result = await saveAndSetDashboard({
            PublishedDetails: { ...activeDashboard?.DraftDetails },
            State: {
                IsPublic: publicState,
                IsPublished: true,
                PublishedDate: new Date().toISOString(),
                IsTemplate: activeDashboard?.State?.IsTemplate,
            },
        });


         const [vo1,vo2] = await Promise.allSettled([
                            getDashboardVisualOptionsList(activeDashboard._id, true),
                            getDashboardVisualOptionsList(activeDashboard._id, false)
                        ]);
        return result;
    };

    const updatePublicState = (publicState = null) => {
        if (activeDashboard?.State?.IsPublished) {
            saveAndSetDashboard({
                State: {
                    ...activeDashboard.State,
                    IsPublic:
                        publicState !== null
                            ? publicState
                            : !activeDashboard.State.IsPublic,
                },
            });
        }
    };

    const cleanFilters = (filters = []) =>
        filters.filter((f) => f?.Column && f?.Operator && f?.Value != null);

    const createVisual = async (visual) => {
        const visuals = activeDashboard?.DraftDetails?.Visuals || [];

        const cleanedVisual = {
            ...visual,
            Filters: cleanFilters(visual?.Filters),
        };

        return await saveAndSetDashboard({
            DraftDetails: {
                ...activeDashboard?.DraftDetails,
                Visuals: [...visuals, cleanedVisual],
            },
        });
    };

    const editVisual = async (visual) => {
        const visuals = activeDashboard?.DraftDetails?.Visuals || [];

        const cleanedVisual = {
            ...visual,
            Filters: cleanFilters(visual?.Filters),
        };

        const updatedVisuals = visuals.map((v) =>
            v.VisualId === visual.VisualId ? cleanedVisual : v
        );

        var result = await saveAndSetDashboard({
            DraftDetails: {
                ...activeDashboard?.DraftDetails,
                Visuals: updatedVisuals,
            },
        });

        await getDashboardVisualOptionsList([activeDashboard._id],false)
        return result;
    };

    const deleteVisual = async (visual) => {
        const visuals = activeDashboard?.DraftDetails?.Visuals || [];

        const updatedVisuals = visuals.filter(
            (v) => v.VisualId !== visual.VisualId
        );

        return await saveAndSetDashboard({
            DraftDetails: {
                ...activeDashboard?.DraftDetails,
                Visuals: updatedVisuals,
            },
        });
    };

    const toggleVisualLock = async (visual) => {
        const visuals = activeDashboard?.DraftDetails?.Visuals || [];

        const updatedVisuals = visuals.map((v) =>
            v.VisualId === visual.VisualId
                ? { ...v, IsLocked: !v.IsLocked }
                : v
        );
        return await saveAndSetDashboard({
            DraftDetails: {
                ...activeDashboard?.DraftDetails,
                Visuals: updatedVisuals,
            },
        },true);
    };

    const editVisualCoordinates = async (oldX, oldY, gridItem) => {
        const visuals = activeDashboard?.DraftDetails?.Visuals || [];
        if (!visuals.length) return;
        const updatedVisuals = visuals.map((visual) =>
            visual.VisualId === gridItem.i
                ? {
                      ...visual,
                      PositionX: gridItem.x,
                      PositionY: gridItem.y,
                      Width: gridItem.w,
                      Height: gridItem.h,
                  }
                : visual
        );

        var result = await saveAndSetDashboard({
            DraftDetails: {
                ...activeDashboard?.DraftDetails,
                Visuals: updatedVisuals,
            },
        },true);

        return result;
    };

    return {
        dashboardList,
        activeDashboard,
        addNewDashToFullList,
        removeActiveDashFromList,
        handleDashNameChange,
        handleDashDescriptionChange,
        handleDashSmallChange,
        clearAllVisuals,
        revertChanges,
        publishDashboard,
        createVisual,
        editVisual,
        deleteVisual,
        toggleVisualLock,
        editVisualCoordinates,
        updatePublicState,
        isLoading,
        hasError,
    };
};
